// Reference-styled JSON tree viewer used by the Final Checker page.
//
// Mirrors the look the ocr-ui reference renders for its BR projection
// JSON view: teal property names, dark-red string values, violet
// numbers, monospace font, collapsible nodes with chevron indicators.
// Separate from `JsonTree.tsx` because that one is shared across other
// inspector panels with their own (emerald-string, gray-key) palette —
// changing it globally would shift those views too.

import { useMemo, useState } from "react";
import {
  ChevronDown,
  ChevronRight,
  ChevronsDownUp,
  ChevronsUpDown,
  Check,
  Copy,
} from "lucide-react";

interface ResponseJsonViewerProps {
  /** Raw JSON-serializable value. */
  value: unknown;
  /** Header label shown above the tree. Default "Response JSON". */
  title?: string;
}

export function ResponseJsonViewer({
  value,
  title = "Response JSON",
}: ResponseJsonViewerProps) {
  const [allExpanded, setAllExpanded] = useState(true);
  const [copied, setCopied] = useState(false);
  // Bumping this key forces the inner tree to remount, picking up the
  // new `initiallyExpanded` default. Cheaper than threading expansion
  // state through every node.
  const [remountToken, setRemountToken] = useState(0);

  const pretty = useMemo(() => {
    try {
      return JSON.stringify(value, null, 2);
    } catch {
      return String(value);
    }
  }, [value]);

  const toggleAll = () => {
    setAllExpanded((v) => !v);
    setRemountToken((t) => t + 1);
  };

  const copyAll = async () => {
    try {
      await navigator.clipboard.writeText(pretty);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard may be blocked; ignore silently */
    }
  };

  return (
    <div className="flex h-full min-h-0 flex-col">
      <header className="flex flex-shrink-0 items-center justify-between border-b border-gray-100 px-3 py-2">
        <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={toggleAll}
            title={allExpanded ? "Collapse all" : "Expand all"}
            aria-label={allExpanded ? "Collapse all" : "Expand all"}
            className="rounded p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
          >
            {allExpanded ? (
              <ChevronsDownUp className="h-3.5 w-3.5" />
            ) : (
              <ChevronsUpDown className="h-3.5 w-3.5" />
            )}
          </button>
          <button
            type="button"
            onClick={copyAll}
            title={copied ? "Copied" : "Copy JSON"}
            aria-label="Copy JSON"
            className="rounded p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
          >
            {copied ? (
              <Check className="h-3.5 w-3.5 text-emerald-600" />
            ) : (
              <Copy className="h-3.5 w-3.5" />
            )}
          </button>
        </div>
      </header>

      <div
        key={remountToken}
        className="flex-1 overflow-auto px-3 py-2 font-mono text-xs leading-relaxed"
      >
        <JsonNode
          value={value}
          depth={0}
          initiallyExpanded={allExpanded}
        />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- helpers

interface JsonNodeProps {
  value: unknown;
  depth: number;
  initiallyExpanded: boolean;
}

function JsonNode({ value, depth, initiallyExpanded }: JsonNodeProps) {
  if (value === null) return <span className="text-gray-400">null</span>;
  if (value === undefined)
    return <span className="text-gray-400">undefined</span>;

  if (typeof value === "string") {
    return (
      <span className="text-rose-700 break-all">"{value}"</span>
    );
  }
  if (typeof value === "number") {
    return <span className="tabular-nums text-violet-700">{value}</span>;
  }
  if (typeof value === "boolean") {
    return <span className="text-amber-700">{String(value)}</span>;
  }

  if (Array.isArray(value)) {
    return (
      <ArrayNode
        items={value}
        depth={depth}
        initiallyExpanded={initiallyExpanded}
      />
    );
  }
  if (typeof value === "object") {
    return (
      <ObjectNode
        entries={Object.entries(value as Record<string, unknown>)}
        depth={depth}
        initiallyExpanded={initiallyExpanded}
      />
    );
  }

  return <span>{String(value)}</span>;
}

function ObjectNode({
  entries,
  depth,
  initiallyExpanded,
}: {
  entries: [string, unknown][];
  depth: number;
  initiallyExpanded: boolean;
}) {
  const [expanded, setExpanded] = useState(initiallyExpanded);
  if (entries.length === 0)
    return <span className="text-gray-400">{"{ }"}</span>;

  return (
    <span>
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="inline-flex items-center align-middle text-gray-500 hover:text-gray-700"
        aria-label={expanded ? "Collapse" : "Expand"}
      >
        {expanded ? (
          <ChevronDown className="h-3 w-3" />
        ) : (
          <ChevronRight className="h-3 w-3" />
        )}
      </button>
      <span className="text-gray-700">{"{"}</span>
      {expanded ? (
        <ul className="ml-3 border-l border-gray-200 pl-3">
          {entries.map(([k, v], i) => (
            <li key={k} className="my-0.5">
              <span className="font-semibold text-teal-700">{k}</span>
              <span className="text-gray-500">: </span>
              <JsonNode
                value={v}
                depth={depth + 1}
                initiallyExpanded={initiallyExpanded}
              />
              {i < entries.length - 1 ? (
                <span className="text-gray-400">,</span>
              ) : null}
            </li>
          ))}
        </ul>
      ) : (
        <span className="text-gray-400">
          {" "}
          {entries.length} key{entries.length === 1 ? "" : "s"}{" "}
        </span>
      )}
      <span className="text-gray-700">{"}"}</span>
    </span>
  );
}

function ArrayNode({
  items,
  depth,
  initiallyExpanded,
}: {
  items: unknown[];
  depth: number;
  initiallyExpanded: boolean;
}) {
  const [expanded, setExpanded] = useState(initiallyExpanded);
  if (items.length === 0) return <span className="text-gray-400">[ ]</span>;

  return (
    <span>
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="inline-flex items-center align-middle text-gray-500 hover:text-gray-700"
        aria-label={expanded ? "Collapse" : "Expand"}
      >
        {expanded ? (
          <ChevronDown className="h-3 w-3" />
        ) : (
          <ChevronRight className="h-3 w-3" />
        )}
      </button>
      <span className="text-gray-700">[</span>
      {expanded ? (
        <ul className="ml-3 border-l border-gray-200 pl-3">
          {items.map((item, i) => (
            <li key={i} className="my-0.5">
              <span className="mr-1 text-gray-400 tabular-nums">{i}</span>
              <span className="text-gray-500">: </span>
              <JsonNode
                value={item}
                depth={depth + 1}
                initiallyExpanded={initiallyExpanded}
              />
              {i < items.length - 1 ? (
                <span className="text-gray-400">,</span>
              ) : null}
            </li>
          ))}
        </ul>
      ) : (
        <span className="text-gray-400">
          {" "}
          {items.length} item{items.length === 1 ? "" : "s"}{" "}
        </span>
      )}
      <span className="text-gray-700">]</span>
    </span>
  );
}
